import { RootViewModel } from '../root-view-model';
import { ExternalViewModelTypeDecorator } from '../decorators/external-view-model-type.decorator';
import { CollectionViewModelTypeDecorator } from '../decorators/collection-view-model-type.decorator';
import { ClassConstructor } from '@nts/std/serialization';
import { NmRootViewModelTypeInspector } from '../decorators/nm/nm-root-view-model-type.decorator';
import { NmItemsViewModelTypeInspector } from '../decorators/nm/nm-items-view-model-type.decorator';
import { ItemViewModel } from '../item-view-model';
import { ExternalViewModel } from '../external-view-model';
import { CollectionViewModel } from '../collection-view-model';
import { takeUntil } from 'rxjs';
import { BaseNMCollection, NmOCCModel } from '../../domain-models';
import type { IdentityInterface, ModelInterface } from '@nts/std/interfaces';
import { MetaDataUtils } from '../../meta-data/meta-data-utils';
import { ViewModelStates } from '@nts/std/types';

export class NmRootViewModel<

    /**
     * Model principale
     */
    TModel extends NmOCCModel<TRootDiscriminator, TIdentity, TItem, TItemIdentity, TOtherExternal, TOtherExternalIdentity, TNMCollection>,

    /**
     * Identity del Model principale
     */
    TIdentity extends IdentityInterface,

    /**
     * Model che rappresenta l'estremità principale della relazione N:M (sarà utilizzato come discriminante per caricare la collection delle associazioni)
     */
    TRootDiscriminator extends ModelInterface<TIdentity>,

    /**
     * Tipo dell'associazione N:M
     */
    TItem extends ModelInterface<TItemIdentity>,

    /**
     * Idenity dell'associazione N:M
     */
    TItemIdentity extends IdentityInterface,

    /**
     * Estremità opposta al discriminatore della relazione N:M
     */
    TOtherExternal extends ModelInterface<TOtherExternalIdentity>,

    /**
     * Idenity dell'estremità opposta al discriminatore della relazione N:M
     */
    TOtherExternalIdentity extends IdentityInterface,

    /**
     * Tipo della collection di associazioni
     */
    TNMCollection extends BaseNMCollection<TItem, TItemIdentity, TRootDiscriminator, TIdentity, TOtherExternal, TOtherExternalIdentity>,

    /**
     * ViewModel che rappresenta l'estremità principale della relazione N:M (sarà utilizzato come discriminante per caricare la collection delle associazioni)
     */
    TRootDiscriminatorViewModel extends ExternalViewModel<TRootDiscriminator, TIdentity>,

    /**
     * ViewModel del tipo dell'associazione N:M
     */
    TItemViewModel extends ItemViewModel<TItem, TItemIdentity>,

    /**
     * CollectionViewModel del tipo della collection di associazioni
     */
    TNMCollectionViewModel extends CollectionViewModel<TItemViewModel, TItem, TItemIdentity>>
    extends RootViewModel<TModel, TIdentity> {

    private _rootViewModelType: ClassConstructor<TRootDiscriminatorViewModel>;

    private _itemsViewModelType: ClassConstructor<TNMCollectionViewModel>;

    @ExternalViewModelTypeDecorator((target: any, propertyKey: string) => {
        return target.rootType;
    })
    root: TRootDiscriminatorViewModel;

    @CollectionViewModelTypeDecorator((target: any, propertyKey: string) => {
        return target.itemsType;
    })
    items: TNMCollectionViewModel;

    get rootType(): ClassConstructor<TRootDiscriminatorViewModel> {
        return this._rootViewModelType;
    }

    get itemsType(): ClassConstructor<TNMCollectionViewModel> {
        return this._itemsViewModelType;
    }

    constructor() {

        super();

        this._rootViewModelType = NmRootViewModelTypeInspector.getValue(this);
        if (this._rootViewModelType === undefined) {
            throw new Error(
                `MetaData ${NmRootViewModelTypeInspector.META_DATA_KEY} not defined. You must use ${NmRootViewModelTypeInspector.DECORATOR_NAME} in ${this.constructor.name}.`
            );
        }

        this._itemsViewModelType = NmItemsViewModelTypeInspector.getValue(this);
        if (this._itemsViewModelType === undefined) {
            throw new Error(
                `MetaData ${NmItemsViewModelTypeInspector.META_DATA_KEY} not defined. You must use ${NmItemsViewModelTypeInspector.DECORATOR_NAME} in ${this.constructor.name}.`
            );
        }

    }

    override async postInit(): Promise<void> {

        this.root.isEnabled = this.currentState == ViewModelStates.New;

        this.root.externalDomainModelChanged.pipe(takeUntil(this.destroySubscribers$)).subscribe(() => {

            // Ciclo tutti gli elementi della collection e setto il discriminante
            this.items.forEach((i) => {
                const extVm = i[MetaDataUtils.toCamelCase(this.domainModel.discriminatorPropertyName)] as TRootDiscriminatorViewModel;
                extVm.setFromModel(this.root.domainModel);
            });
        });
    }

}
